import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { Check, MapPin, MessageCircle } from "lucide-react";
import { useApp } from "./context";
import { t, STRINGS } from "./i18n";

const WHATSAPP_NUMBER = "38269409419"; // +382 69 409 419

const markerIcon = L.divIcon({
  html: '<div class="stilet-marker active"></div>',
  className: "", iconSize: [28, 28], iconAnchor: [14, 14],
});

export default function ApartmentDetailPage() {
  const { slug } = useParams();
  const { lang, API } = useApp();
  const [apt, setApt] = useState(null);
  const [error, setError] = useState(null);

  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState(2);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [extras, setExtras] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/apartments/${slug}`).then(r => setApt(r.data)).catch(() => setError("Not found"));
  }, [slug, API]);

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const a = new Date(checkIn), b = new Date(checkOut);
    return Math.max(0, Math.round((b - a) / 86400000));
  }, [checkIn, checkOut]);

  const total = apt ? nights * apt.price_per_night : 0;

  const extraOptions = STRINGS.services.items;

  const toggleExtra = (k) => setExtras(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!checkIn || !checkOut || nights <= 0) { setSubmitError("Please choose valid dates"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        apartment_id: apt.id, check_in: checkIn, check_out: checkOut, guests,
        full_name: fullName, email, phone, message, extra_services: extras,
        total_price: total,
      });
      setSuccess(true);
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) return <div className="section-pad container-x">Not found. <Link to="/apartments" className="btn-link">Back</Link></div>;
  if (!apt) return <div className="section-pad container-x">Loading…</div>;

  const waMessage = encodeURIComponent(`Hello! I'm interested in booking ${apt.name} at Stilet Residence.`);
  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${waMessage}`;

  return (
    <div data-testid="page-detail">
      {/* Floating WhatsApp */}
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float"
        title="Chat on WhatsApp"
        style={{
          position: 'fixed', right: '24px', bottom: '24px', zIndex: 60,
          width: '56px', height: '56px',
          background: '#25D366', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '50%',
          boxShadow: '0 10px 28px rgba(37, 211, 102, 0.45)',
          transition: 'transform 250ms ease-out',
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        <MessageCircle size={26} strokeWidth={1.8} fill="#fff" stroke="#25D366" />
      </a>
      {/* GALLERY */}
      <section className="container-x pt-10">
        <div className="overline">{lang === "en" ? apt.location_name_en : apt.location_name_sr}</div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight mt-3" data-testid="detail-title">{apt.name}</h1>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="detail-gallery">
          <div className="md:col-span-2 md:row-span-2">
            <img src={apt.images[0]} alt="" className="w-full h-full object-cover" style={{ aspectRatio: '4/3' }} />
          </div>
          {apt.images.slice(1, 3).map((url) => (
            <div key={url} className="md:col-span-2">
              <img src={url} alt="" className="w-full h-full object-cover" style={{ aspectRatio: '8/3' }} />
            </div>
          ))}
        </div>
      </section>

      {/* INFO + BOOKING */}
      <section className="container-x mt-16 grid grid-cols-1 lg:grid-cols-5 gap-12 pb-24">
        <div className="lg:col-span-3 space-y-12">
          <div>
            <div className="overline">{t("details.overview", lang)}</div>
            <div className="divider-line my-4" />
            <p className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {lang === "en" ? apt.description_en : apt.description_sr}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="amenity-pill">{apt.rooms} {t("details.rooms", lang)}</span>
              <span className="amenity-pill">{apt.beds} {t("details.beds", lang)}</span>
              <span className="amenity-pill">{apt.bathrooms} {t("details.baths", lang)}</span>
              <span className="amenity-pill">{apt.size_sqm} {t("details.sqm", lang)}</span>
              <span className="amenity-pill">{apt.max_guests} {t("details.guests", lang)}</span>
            </div>
          </div>

          <div>
            <div className="overline">{t("details.amenities", lang)}</div>
            <div className="divider-line my-4" />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {apt.amenities.map(a => (
                <div key={a} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <Check size={16} strokeWidth={1.5} style={{ color: 'var(--accent-primary)' }} /> {a}
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="overline">{t("details.house_rules", lang)}</div>
            <div className="divider-line my-4" />
            <p style={{ color: 'var(--text-secondary)' }}>{lang === "en" ? apt.rules_en : apt.rules_sr}</p>
          </div>

          <div>
            <div className="overline">{t("details.location", lang)}</div>
            <div className="divider-line my-4" />
            <p className="flex items-center gap-2 mb-4" style={{ color: 'var(--text-secondary)' }}>
              <MapPin size={16} strokeWidth={1.5} /> {apt.address}
            </p>
            <div style={{ height: 360, border: '1px solid var(--border)' }} data-testid="detail-map">
              <MapContainer center={[apt.lat, apt.lng]} zoom={15} style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" attribution="&copy; OSM, CARTO" />
                <Marker position={[apt.lat, apt.lng]} icon={markerIcon} />
              </MapContainer>
            </div>
          </div>
        </div>

        {/* BOOKING WIDGET */}
        <aside className="lg:col-span-2">
          <div className="lg:sticky lg:top-28 p-8" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }} data-testid="booking-widget">
            <div className="flex items-baseline justify-between mb-6">
              <h3 className="font-display text-2xl">{t("details.book_title", lang)}</h3>
              <span><span className="font-display text-3xl">€{apt.price_per_night}</span> <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t("listings.night", lang)}</span></span>
            </div>

            {success ? (
              <div className="p-6" style={{ background: '#E1EFE5', border: '1px solid #1F4A30' }} data-testid="booking-success">
                <p style={{ color: '#1F4A30' }}>{t("details.success", lang)}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="field-label">{t("details.check_in", lang)}</label>
                    <input type="date" className="input-base" value={checkIn} onChange={e => setCheckIn(e.target.value)} required data-testid="booking-checkin" />
                  </div>
                  <div>
                    <label className="field-label">{t("details.check_out", lang)}</label>
                    <input type="date" className="input-base" value={checkOut} onChange={e => setCheckOut(e.target.value)} required data-testid="booking-checkout" />
                  </div>
                </div>
                <div>
                  <label className="field-label">{t("details.guests", lang)}</label>
                  <select className="input-base" value={guests} onChange={e => setGuests(parseInt(e.target.value))} data-testid="booking-guests">
                    {Array.from({length: apt.max_guests}, (_,i)=>i+1).map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>

                <div>
                  <label className="field-label">{t("details.extras", lang)}</label>
                  <div className="space-y-2">
                    {extraOptions.map(opt => (
                      <label key={opt.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`extra-${opt.key}`}>
                        <input type="checkbox" checked={extras.includes(opt.key)} onChange={() => toggleExtra(opt.key)} />
                        <span>{opt[lang][0]} <span style={{ color: 'var(--text-muted)' }}>· {opt[lang][2]}</span></span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                  <label className="field-label">{t("details.your_info", lang)}</label>
                  <input className="input-base mb-2" placeholder={t("details.full_name", lang)} value={fullName} onChange={e=>setFullName(e.target.value)} required data-testid="booking-name" />
                  <input className="input-base mb-2" type="email" placeholder={t("details.email", lang)} value={email} onChange={e=>setEmail(e.target.value)} required data-testid="booking-email" />
                  <input className="input-base mb-2" placeholder={t("details.phone", lang)} value={phone} onChange={e=>setPhone(e.target.value)} data-testid="booking-phone" />
                  <textarea className="input-base" rows={3} placeholder={t("details.message_opt", lang)} value={message} onChange={e=>setMessage(e.target.value)} data-testid="booking-message" />
                </div>

                {nights > 0 && (
                  <div className="flex justify-between items-baseline pt-4 border-t" style={{ borderColor: 'var(--border)' }} data-testid="booking-total">
                    <span style={{ color: 'var(--text-muted)' }}>{nights} {t("details.nights", lang)} × €{apt.price_per_night}</span>
                    <span className="font-display text-2xl">€{total}</span>
                  </div>
                )}

                {submitError && <div className="text-sm" style={{ color: '#7A1F1F' }} data-testid="booking-error">{String(submitError)}</div>}

                <button type="submit" className="btn-primary w-full justify-center" disabled={submitting} data-testid="booking-submit">
                  {submitting ? "..." : t("details.book_btn", lang)}
                </button>
              </form>
            )}
          </div>
        </aside>
      </section>
    </div>
  );
}
